import { Dialog } from "@headlessui/react";
import { ModalFoterBox } from "../../components/box/Box";
import Button from "../../components/button/Button";
import { Modal, ModalProp } from "../../components/modal/Modal";
import { SetStateAction } from "react";
import { useDispatch } from "react-redux";
import { AlertValue, alertOpen } from "../../features/alert.slice";
import { ConstMessage } from "../../config/const";
import { Company } from "../../model/Companies";
import { convertDateToString, convertDateTimeToStringCompactFormat } from "../../utility/ConvertUtily";
import { PointHistoryData } from "../../model/PointData";
import { auth } from "../../firebase";
import { requestGetPointHistory } from "../../api/Point.api";

export type PointHistoryExportModalProps = {
  setLoadProgress: (value: SetStateAction<number>) => void;
  company: Company | undefined;
  pointHistoryList: PointHistoryData[];
  dispName: string;
  userId?: string | undefined;
  searchCondition?: any | [];
}

export const PointHistoryExportModal = ({
  open,
  toggle,
  closeOnClickOutside,
  setLoadProgress,
  company,
  searchCondition
}: ModalProp & PointHistoryExportModalProps) => {

  const dispatch = useDispatch();
  // ログイン中の団体管理者情報
  const currentUser = auth.currentUser;

  const csvCol = [
    "利用者番号",
    "名前",
    "カテゴリ",
    "タイプ",
    "ポイント名",
    "有効期限",
    "ポイント発生日",
    "取引種別",
    "ポイント数",
    "保有ポイント数",
  ];

  // CSV出力処理
  async function exportCsvHandler () {
    toggle();
    setLoadProgress(0);
    const result = await exportCsv();
    setLoadProgress(-1);
    if(!result.result){
      console.log(result.details);
      dispatch(alertOpen(AlertValue(ConstMessage.ERR_CSV_EXPORT, "danger")));
    }
  };

  const exportCsv = async () => {
    try {
      const allHistory: PointHistoryData[] = [];
      let page = 1;
      let hasMore = true;
      const pageSize = 5000;
      let totalCount = 0;

      while (hasMore) {
        const result = await requestGetPointHistory(page, pageSize, searchCondition);
        if (!result.success) {
          console.error("ポイント履歴取得に失敗", result.error);
          return { result: false, details: result.error };
        }
        if (page === 1) {
          totalCount = result.totalCount ?? 0;
        }
        allHistory.push(...result.pointHistory);

        hasMore = result.pointHistory.length === pageSize;

        // 進捗更新
        const progressRate = Math.round((allHistory.length / totalCount) * 50);
        setLoadProgress(progressRate);

        page++;
        await new Promise((resolve) => setTimeout(resolve, 0));
      }

      // ポイント発生日の降順にする
      const pointHistorySortList = allHistory.sort((a, b) => {
        if (a.occurrenceDate < b.occurrenceDate) return 1;
        if (a.occurrenceDate > b.occurrenceDate) return -1;
        if (a.displayUserId > b.displayUserId) return 1;
        if (a.displayUserId < b.displayUserId) return -1;
        return 0;
      });

      // CSV生成
      const csvData = pointHistorySortList.map((value) => [
        value.displayUserId,
        value.userName,
        value.ptCategoryName ?? "",
        value.ptTypeName ?? "",
        value.ptName ?? "",
        value.expirationDate ? convertDateToString(value.expirationDate) : "",
        convertDateToString(value.occurrenceDate),
        value.tranType,
        value.grantPoints,
        value.heldPoints ?? "",
      ]);

      // 文字列のエスケープ処理
      const escapeCsvData = csvData.map((row, index) => {
        setLoadProgress(50 + Math.round((index / csvData.length) * 50)); // 50〜100%
        return row.map((val) => `"${String(val).replace(/"/g, '""')}"`);
      });

      const escapeCsvCol = csvCol.map((value) => `"${value.replace(/"/g, '""')}"`);
      // カラムとデータを連結
      const csvStr = escapeCsvCol.join(',') + "\n" + escapeCsvData.map((row) => row.join(',')).join('\n');
      // CSVファイル名
      const fileName = `ポイント履歴_${company?.name}_${convertDateTimeToStringCompactFormat(new Date())}.csv`;
      const link = document.createElement("a");
      const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
      const blob = new Blob([bom, csvStr], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setLoadProgress(-1);
      return { result: true, details: undefined };
    } catch (err) {
      setLoadProgress(-1);
      return { result: false, details: err };
    }
  };

  return (
    <>
      <Modal
        open={open}
        toggle={toggle}
        closeOnClickOutside={closeOnClickOutside}
      >
        <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white  text-left shadow-xl transition-all sm:w-full max-w-screen-md ">
          {(
            <>
              <div className="px-2 pb-4 sm:p-4">
                <div className="text-center sm:mt-3">
                  <Dialog.Title
                    as="h3"
                    className="text-left text-lg font-medium leading-6 text-gray-900"
                  >
                    ポイント履歴出力確認
                  </Dialog.Title>
                </div>
                <div className="mt-5">
                  ポイント履歴の検索結果情報をCSVファイルで出力します。
                </div>
              </div>
              <div className={'text-red-500'}>
                　※CSV出力は、件数によって<span className={"font-semibold"}>作業時間が長時間になる場合があります。</span><br/>
                　※一度実行すると中止することはできません。
              </div>
              <br></br>
              <ModalFoterBox>
                <Button
                  size={'md'}
                  className={"w-[132px]"}
                  outline
                  onClick={toggle}
                >
                  キャンセル
                </Button>
                <Button
                  size={'md'}
                  className={"ml-2 w-[132px]"}
                  onClick={exportCsvHandler}
                >
                  OK 
                </Button>
              </ModalFoterBox>
            </>
          )}
        </Dialog.Panel>
      </Modal>
    </>
  );
}
